import { Badge, Card, CardBody, DataTable, DateDisplay, matchesExactKey, Tooltip } from "@aidkitorg/component-library";
import { ColumnDef } from "@tanstack/react-table";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSpinDelay } from "spin-delay";
import { useAuditLogs } from "../../../Contexts/AuditLogsContext";
import { useAsyncEffect } from "../../../Hooks/AsyncEffect";
import { useLocalizedStrings } from "../../../Localization";
import { AuditLogModal } from "./AuditLogModal";
import { AuditLogFilters } from "./filters/AuditLogFilters";
import { EyeIcon } from "@heroicons/react/24/solid";
import { AuditLog } from "../types";
import { getBadgeVariant } from "../utils";
import type { AuditLogs } from '@aidkitorg/types/lib/survey';

export const AuditLogsDataTable: React.FC<{
  pageConfig?: AuditLogs
  isConfigLoading: boolean
}> = ({ pageConfig, isConfigLoading }) => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const location = useLocation();
  
  const {
    auditLogs,
    getAuditLogs,
    isAuditLogsLoading,
    isAuditLogsSearching,
    total,
    page,
    limit,
    totalPages,
    auditLogsSearchDisplay,
    auditLogsSorting,
    onAuditLogsPaginationChange,
    onAuditLogsSearchChange,
    onAuditLogsSortingChange
  } = useAuditLogs();
  
  const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // Use spin-delay to prevent loading flashes
  const showLoadingSpinner = useSpinDelay(isAuditLogsLoading && hasInitialLoad, {
    delay: 250,
    minDuration: 150
  });
  
  const showSearchSpinner = useSpinDelay(isAuditLogsSearching && hasInitialLoad, {
    delay: 250,
    minDuration: 150
  });

  // URL parameter utilities
  const getUrlSearchParam = (key: string): string => {
    const params = new URLSearchParams(location.search);
    return params.get(key) || '';
  };

  const setUrlSearchParam = useCallback((key: string, value: string) => {
    const params = new URLSearchParams(location.search);
    if (value.trim()) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  // Enhanced search change handler with URL encoding
  const handleSearchChange = useCallback((search: string) => {
    setUrlSearchParam('search', search); // Update URL immediately
    onAuditLogsSearchChange(search); // Call the original debounced handler
  }, [setUrlSearchParam, onAuditLogsSearchChange]);

  useAsyncEffect(async () => {
    // Initialize search from URL on first load
    const urlSearch = getUrlSearchParam('search');
    if (urlSearch && urlSearch !== auditLogsSearchDisplay) {
      // Use the original search change handler to properly initialize state
      onAuditLogsSearchChange(urlSearch);
    } else {
      await getAuditLogs({ page: 1, limit: 10 });
    }
    setHasInitialLoad(true);
  }, []);

  const ALL_COLUMNS: ColumnDef<AuditLog>[] = [
    {
      accessorKey: "timestamp",
      header: L.admin_panel.audit_logs.timestamp,
      enableSorting: true,
      size: 30,
      cell: ({ row }) => {
        return (
          <span>
            <DateDisplay utcValue={row.original.timestamp} />
          </span>
        );
      },
    },
    {
      accessorKey: "event",
      header: L.admin_panel.audit_logs.event,
      enableSorting: true,
      size: 35,
      minSize: 200, // Set minimum column width to prevent layout shifts
      cell: ({ row }) => {
        const event = row.original.event;
        const formattedEvent = L.admin_panel.audit_logs.events[event as keyof typeof L.admin_panel.audit_logs.events] || event;
        return (
          <div className="flex justify-start">
            <Badge variant={getBadgeVariant(event)} style="pill" className="min-w-fit">
              {formattedEvent}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "userName",
      header: L.admin_panel.audit_logs.user,
      enableSorting: true,
      size: 25,
      cell: ({ row }) => {
        const userName = row.original.userName;

        return <span>{userName}</span>;
      },
    },
    {
      accessorKey: "uid",
      header: L.admin_panel.audit_logs.uid,
      enableSorting: true,
      size: 20,
      cell: ({ row }) => {
        const uid = row.original.uid;

        return <span>{uid}</span>;
      },
    },
    {
      accessorKey: "userEmail",
      header: L.admin_panel.audit_logs.email,
      enableSorting: true,
      size: 25,
      cell: ({ row }) => {
        const userEmail = row.original.userEmail;

        return <span>{userEmail}</span>;
      },
    },
    {
      id: "view",
      header: L.admin_panel.audit_logs.view,
      size: 10,
      cell: ({ row }) => {
        return (
          <Tooltip text={L.admin_panel.audit_logs.view_tooltip}>
            <button
              onClick={() => setSelectedAuditLog(row.original)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="View audit log details"
            >
              <EyeIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        );
      },
    },
  ];

  // Determine which columns to show based on pageConfig
  const columns = useMemo(() => {
    if (!pageConfig?.defaultTableColumns) {
      // If no config, show nothing
      return [];
    }

    const config = pageConfig.defaultTableColumns;
    return ALL_COLUMNS.filter((column) => {
      // Only filter columns that have an accessorKey
      if (!('accessorKey' in column)) return true;

      const key = column.accessorKey as string;

      // Check config for columns
      if (key === 'timestamp') return config.timestamp !== 'hidden';
      if (key === 'event') return config.eventType !== 'hidden';
      if (key === 'userName') return config.userName !== 'hidden';
      if (key === 'uid') return config.userId !== 'hidden';
      if (key === 'userEmail') return config.userEmail !== 'hidden';

      // Default: show column if not explicitly configured
      return true;
    });
  }, [ALL_COLUMNS, pageConfig]);

  // Determine if there are any available filters based on pageConfig
  const hasAvailableFilters = useMemo(() => {
    if (!pageConfig?.defaultTableColumns) {
      // If no config, all filters are available by default
      return true;
    }

    const columns = pageConfig.defaultTableColumns;
    // Check if at least one filterable column is visibleAndFilterable
    return columns.timestamp === 'visibleAndFilterable' ||
           columns.eventType === 'visibleAndFilterable' ||
           columns.userName === 'visibleAndFilterable';
  }, [pageConfig]);

  // Keyboard shortcuts for pagination
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when not typing in an input/textarea
      if (event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement) {
        return;
      }

      // Handle Alt+ArrowLeft for previous page
      if (matchesExactKey(event, { key: 'ArrowLeft', alt: true }) && page > 1) {
        event.preventDefault();
        onAuditLogsPaginationChange({
          pageIndex: page - 2, // Convert to 0-based index
          pageSize: limit
        });
      }

      // Handle Alt+ArrowRight for next page
      if (matchesExactKey(event, { key: 'ArrowRight', alt: true }) && totalPages && page < totalPages) {
        event.preventDefault();
        onAuditLogsPaginationChange({
          pageIndex: page, // Already correct for 0-based index
          pageSize: limit
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [page, totalPages, limit, onAuditLogsPaginationChange]);

  return (
    <>
      <Card className="rounded-md w-full h-full">
        <CardBody>
          <DataTable
            columns={columns}
            data={auditLogs}
            initializing={!hasInitialLoad}
            loading={showLoadingSpinner || showSearchSpinner || isConfigLoading}
            enableSearch
            searchLabel={L.admin_panel.audit_logs.search_audit_logs}
            manualSearch={{
              searchValue: auditLogsSearchDisplay,
              onSearchChange: handleSearchChange,
              resetPageOnChange: false
            }}
            showPagination
            pageSize={limit}
            manualPagination={{
              pageCount: totalPages || 0,
              pageIndex: Math.max(0, page - 1), // Convert from 1-based to 0-based
              totalRows: total || 0,
              onPaginationChange: onAuditLogsPaginationChange,
            }}
            manualSorting={{
              sorting: auditLogsSorting,
              onSortingChange: onAuditLogsSortingChange,
            }}
            filters={hasAvailableFilters ? () => (
              <AuditLogFilters
                defaultTableColumns={pageConfig?.defaultTableColumns}
              />
            ) : undefined}
          />
        </CardBody>
      </Card>
      <AuditLogModal
        auditLog={selectedAuditLog}
        isOpen={!!selectedAuditLog}
        onClose={() => setSelectedAuditLog(null)}
      />
    </>
  );
};
